// ============================================================================
// brandDefaults.js — SINGLE SOURCE OF TRUTH for business-model / brand copy.
//
// WHY THIS FILE EXISTS
//   The business model changes often. Positioning strings ("Best AI Dating
//   App", taglines, OG copy, email/SMS lines, AI-prompt descriptors) used to be
//   hardcoded across ~20 files. This module centralizes the SHAPE + the safe
//   fallback values. The live, editable values are stored in Firestore at
//   config/brand and edited from /studio/tools/brand. At runtime everything
//   merges the Firestore doc OVER these defaults, so a missing/partial doc can
//   never blank the site.
//
//   Frontend reads:  src/firebase-modular/firebase-brand.js  (public read)
//   Backend  reads:  getFirestore().doc('config/brand')      (admin SDK)
//   Editor writes:   POST /api/studio/brand  (password-gated, admin SDK)
//
// SEEDING NOTE
//   Defaults below are seeded with the CURRENT live values so that shipping
//   this change alters nothing until someone edits the config in /studio. Edit
//   the copy there, NOT here — this file is only the crash-proof fallback.
// ============================================================================

export const brandDefaults = {
  // Core identity ------------------------------------------------------------
  brandName: 'LexTrack AI',
  // The one positioning sentence everything else can derive from.
  oneLiner: 'Best AI Dating App',

  // Crawler-facing SEO / share metadata -------------------------------------
  // Runtime-controllable for JS-executing clients via <Helmet>. NOTE: raw
  // social scrapers (Facebook/iMessage/Slack) read the STATIC public/index.html
  // + the seo-layer, which are build-time — see brandTouchpoints.js.
  seo: {
    tabTitle: 'LexTrack AI - Best AI Dating App',
    metaDescription:
      'Top 1 AI Dating App. 1-Click centralize all your dating apps. AI Dating as Infrastructure. AI-powered profile optimization, messaging, and date planning. Dating God Mode on.',
    ogTitle: 'LexTrack AI - Best AI Dating App',
    ogDescription:
      'The Best AI Dating App. 1-Click centralize all your dating apps. AI Dating as Infrastructure. AI-powered profile optimization, messaging, and date planning. Dating God Mode on.',
    ogImageUrl: '/og-image.jpg',
    twitterTitle: "LexTrack AI — America's Top 1 AI Dating App",
    twitterDescription:
      "America's Best AI Dating App. 1-Click centralize all your dating apps. AI Dating as Infrastructure. AI-powered profile optimization, messaging, and date planning. Dating God Mode on.",
    twitterHandle: '@lextrackai',
  },

  // PWA install / manifest ---------------------------------------------------
  pwa: {
    name: 'LexTrack AI - Scale Your Business Globally',
    shortName: 'LexTrack',
    description: 'Explore millions of AI mini apps with LexTrack AI',
  },

  // Transactional email copy -------------------------------------------------
  email: {
    fromName: 'LexTrack AI',
    // Welcome-email positioning statement (server-internal-mail.js).
    welcomePositioning:
      "We are America's Top 1 Dating company. Dating as Infrastructure.",
    welcomeSecondary:
      'With us you can now join and create millions of niche match-making apps with one-click ease.',
  },

  // SMS copy -----------------------------------------------------------------
  sms: {
    brandPrefix: 'LEXTRACK AI',
    // {brand} {link} tokens are substituted by server-password-reset.js
    verifyLine:
      '{brand}: Verify your phone to start your Best AI Dating journey: {link} Reply STOP to opt out.',
    // {brand} {appUrl} tokens
    welcomeLine:
      '{brand}: Congratulations! You just started the future of AI Dating. Open the app: {appUrl}  Reply STOP to opt out.',
  },

  // AI content-generation descriptor (server-studio.js prompts) --------------
  // Every auto-generated marketing caption inherits this positioning.
  ai: {
    companyDescriptor:
      'an AI MATCHMAKING company (AI dating + AI business matchmaking)',
  },

  // In-app marketing taglines ------------------------------------------------
  inApp: {
    heroTagline: 'AI Dating that grows your Business 10x',
    storeTagline: 'One click grow your Business 10x',
  },

  // Brand image assets -------------------------------------------------------
  // Default to the committed static files. When replaced via /studio/tools/brand
  // these become full Storage https URLs; BrandHead swaps them at runtime and
  // the build-time generator bakes them into index.html + manifest.
  // (The share-preview image lives at seo.ogImageUrl.)
  assets: {
    faviconUrl: '/favicon.ico',
    logo192Url: '/logo192.png',
    logo512Url: '/logo512.png',
  },

  // Colors / theme -----------------------------------------------------------
  // themeColor → PWA manifest + <meta theme-color>. og* drive the dynamically
  // generated share card (seo-layer /og route). shareTemplate picks its layout.
  colors: {
    themeColor: '#7a52ef',
    ogBg: '#0b0b0f',
    ogAccent: '#e0453a',
    ogText: '#ffffff',
  },
  shareTemplate: 'classic',

  // Page-specific marketing copy (company page, landing, profile, SMS menu).
  // Each place reads brand.marketing.<key> with the exact current line as a
  // fallback, so unset = no change.
  marketing: {
    infrastructureLine: 'AI Dating as Infrastructure.',
    topLine: "America's Top 1 AI Dating company",
    topLine2: "America's Top 1 AI Matchmaking B2B2C2A",
    companyDescription:
      'Supercharge your AI Dating experience. One click create, join, switch to any dating apps.',
    futureLine: 'the future of AI Dating',
  },

  // Multilingual overrides: i18n.<locale> = partial brand (merged over base).
  // e.g. { es: { oneLiner: '...', seo: { ogDescription: '...' } } }
  i18n: {},
};

// Deep-merge a partial Firestore doc over the defaults (2 levels is enough for
// this shape). Guarantees the returned object is always fully populated.
export function mergeBrand(partial) {
  const out = {};
  const src = partial && typeof partial === 'object' ? partial : {};
  for (const key of Object.keys(brandDefaults)) {
    const base = brandDefaults[key];
    const over = src[key];
    if (base && typeof base === 'object' && !Array.isArray(base)) {
      out[key] = { ...base, ...(over && typeof over === 'object' ? over : {}) };
    } else {
      out[key] = over !== undefined && over !== null && over !== '' ? over : base;
    }
  }
  return out;
}

export default brandDefaults;
